// src/admin/pages/AdminSuppliers.js
import React, { useState, useEffect, useCallback } from 'react';
import { showToast } from '../../contexts/ToastNotification';
import axios from 'axios';
import './admin-suppliers.scss';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function AdminSuppliers() {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [formData, setFormData] = useState({
        nome: '',
        partita_iva: '',
        indirizzo: '',
        citta: '',
        cap: '',
        provincia: '',
        telefono: '',
        email: '',
        contatto: '',
        note: '',
        attivo: true
    });
    const [filters, setFilters] = useState({ search: '', attivo: '' });
    const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });

    // Carica fornitori
    const loadSuppliers = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${API_URL}/admin/suppliers?page=${pagination.page}&limit=20`;
            if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
            if (filters.attivo !== '') url += `&attivo=${filters.attivo}`;
            
            // ✅ Convertito a axios
            const response = await axios.get(url, { withCredentials: true });
            const data = response.data;
            
            if (data.success) {
                setSuppliers(data.suppliers);
                setPagination(prev => ({
                    ...prev,
                    total: data.pagination.total,
                    totalPages: data.pagination.totalPages
                }));
            } else {
                // Dati di esempio per sviluppo
                setSuppliers([
                    { id: 1, nome: 'Cantina Talamonti', partita_iva: 'IT01234567890', indirizzo: 'Via Nazionale 1', citta: 'Loreto Aprutino', cap: '65014', provincia: 'PE', telefono: '0851234567', email: 'info@talamonti.it', contatto: 'Mario Rossi', note: 'Fornitore di vini pregiati', attivo: true, data_registrazione: '2023-01-15' },
                    { id: 2, nome: 'Azienda Agricola Valle Reale', partita_iva: 'IT09876543210', indirizzo: 'Contrada Pozzilli 5', citta: 'Poppi', cap: '65020', provincia: 'PE', telefono: '0857654321', email: 'commerciale@valle-reale.it', contatto: 'Laura Bianchi', note: '', attivo: true, data_registrazione: '2023-02-20' },
                    { id: 3, nome: 'Cantine Bosco', partita_iva: 'IT11223344556', indirizzo: 'Via del Vino 12', citta: 'Manoppello', cap: '65010', provincia: 'PE', telefono: '0859988776', email: 'info@cantinebosco.com', contatto: 'Giuseppe Verdi', note: 'Prodotti biologici', attivo: false, data_registrazione: '2023-03-10' }
                ]);
                setPagination(prev => ({ ...prev, total: 3, totalPages: 1 }));
            }
        } catch (error) {
            console.error('Error loading suppliers:', error);
            showToast('Errore di connessione', 'error');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, filters.search, filters.attivo]);

    useEffect(() => {
        loadSuppliers();
    }, [loadSuppliers]);

    // Resetta form
    const resetForm = () => {
        setFormData({
            nome: '',
            partita_iva: '',
            indirizzo: '',
            citta: '',
            cap: '',
            provincia: '',
            telefono: '',
            email: '',
            contatto: '',
            note: '',
            attivo: true
        });
        setEditingSupplier(null);
    };

    // Apri modal creazione
    const openCreateModal = () => {
        resetForm();
        setShowModal(true);
    };

    // Apri modal modifica
    const openEditModal = (supplier) => {
        setEditingSupplier(supplier);
        setFormData({
            nome: supplier.nome,
            partita_iva: supplier.partita_iva,
            indirizzo: supplier.indirizzo || '',
            citta: supplier.citta || '',
            cap: supplier.cap || '',
            provincia: supplier.provincia || '',
            telefono: supplier.telefono || '',
            email: supplier.email || '',
            contatto: supplier.contatto || '',
            note: supplier.note || '',
            attivo: supplier.attivo
        });
        setShowModal(true);
    };

    // Salva fornitore
    const saveSupplier = async () => {
        if (!formData.nome || !formData.partita_iva) {
            showToast('Inserisci nome e partita IVA', 'warning');
            return;
        }

        try {
            const url = editingSupplier 
                ? `${API_URL}/admin/suppliers/${editingSupplier.id}`
                : `${API_URL}/admin/suppliers`;
            
            // ✅ Convertito a axios
            let response;
            if (editingSupplier) {
                response = await axios.put(url, formData, { withCredentials: true });
            } else {
                response = await axios.post(url, formData, { withCredentials: true });
            }
            
            const data = response.data;
            
            if (data.success) {
                showToast(editingSupplier ? 'Fornitore aggiornato con successo' : 'Fornitore creato con successo', 'success');
                setShowModal(false);
                resetForm();
                loadSuppliers();
            } else {
                showToast(data.message || 'Errore nel salvataggio', 'error');
            }
        } catch (error) {
            console.error('Error saving supplier:', error);
            showToast('Errore di connessione', 'error');
        }
    };

    // Elimina fornitore
    const deleteSupplier = async (id) => {
        if (!window.confirm('Sei sicuro di voler eliminare questo fornitore?')) return;
        
        try {
            // ✅ Convertito a axios
            const response = await axios.delete(`${API_URL}/admin/suppliers/${id}`, { withCredentials: true });
            const data = response.data;
            
            if (data.success) {
                showToast('Fornitore eliminato con successo', 'success');
                loadSuppliers();
            } else {
                showToast(data.message || 'Errore nell\'eliminazione', 'error');
            }
        } catch (error) {
            console.error('Error deleting supplier:', error);
            showToast('Errore di connessione', 'error');
        }
    };

    // Toggle stato fornitore
    const toggleSupplierStatus = async (id, currentStatus) => {
        try {
            // ✅ Convertito a axios
            const response = await axios.put(
                `${API_URL}/admin/suppliers/${id}/toggle`,
                { attivo: !currentStatus },
                { withCredentials: true }
            );
            
            const data = response.data;
            
            if (data.success) {
                showToast(`Fornitore ${!currentStatus ? 'attivato' : 'disattivato'}`, 'success');
                loadSuppliers();
            } else {
                showToast(data.message || 'Errore', 'error');
            }
        } catch (error) {
            console.error('Error toggling supplier:', error);
            showToast('Errore di connessione', 'error');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/D';
        const date = new Date(dateString);
        return date.toLocaleDateString('it-IT');
    };

    if (loading && suppliers.length === 0) {
        return (
            <div className="admin-loading">
                <div className="loader"></div>
                <span>Caricamento fornitori...</span>
            </div>
        );
    }

    return (
        <div className="admin-suppliers">
            {/* Header */}
            <div className="suppliers-header">
                <div className="header-title">
                    <h2>🏭 Gestione Fornitori</h2>
                    <p>Gestisci i fornitori di vini, spumanti e prodotti</p>
                </div>
                <div className="header-actions">
                    <button className="create-btn" onClick={openCreateModal}>
                        + Nuovo Fornitore
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="suppliers-filters">
                <div className="search-wrapper">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        placeholder="Cerca per nome, partita IVA o città..."
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                        className="search-input"
                    />
                </div>
                <select
                    value={filters.attivo}
                    onChange={(e) => setFilters(prev => ({ ...prev, attivo: e.target.value, page: 1 }))}
                    className="filter-select"
                >
                    <option value="">✅ Tutti</option>
                    <option value="true">Attivi</option>
                    <option value="false">Disattivi</option>
                </select>
            </div>

            {/* Stats Cards */}
            <div className="suppliers-stats">
                <div className="stat-card">
                    <div className="stat-icon">🏭</div>
                    <div className="stat-value">{suppliers.length}</div>
                    <div className="stat-label">Totale Fornitori</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">✅</div>
                    <div className="stat-value">{suppliers.filter(s => s.attivo).length}</div>
                    <div className="stat-label">Attivi</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">📦</div>
                    <div className="stat-value">{suppliers.filter(s => s.note).length}</div>
                    <div className="stat-label">Con note</div>
                </div>
            </div>

            {/* Suppliers Table */}
            <div className="suppliers-table-container">
                <table className="suppliers-table">
                    <thead>
                        <tr>
                            <th>Nome</th>
                            <th>Partita IVA</th>
                            <th>Indirizzo</th>
                            <th>Contatti</th>
                            <th>Referente</th>
                            <th>Data Reg.</th>
                            <th>Stato</th>
                            <th>Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {suppliers.map(supplier => (
                            <tr key={supplier.id} className={!supplier.attivo ? 'inactive' : ''}>
                                <td>
                                    <div className="supplier-name">
                                        <span className="supplier-icon">🏭</span>
                                        <span>{supplier.nome}</span>
                                    </div>
                                </td>
                                <td>{supplier.partita_iva}</td>
                                <td>
                                    <div className="supplier-address">
                                        <div>{supplier.indirizzo}</div>
                                        <div className="address-location">
                                            {supplier.citta && `${supplier.citta}, ${supplier.cap || ''}`}
                                            {supplier.provincia && ` (${supplier.provincia})`}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div className="supplier-contacts">
                                        {supplier.telefono && <div>📞 {supplier.telefono}</div>}
                                        {supplier.email && <div>✉️ {supplier.email}</div>}
                                    </div>
                                </td>
                                <td>{supplier.contatto || '—'}</td>
                                <td>{formatDate(supplier.data_registrazione)}</td>
                                <td>
                                    <button
                                        onClick={() => toggleSupplierStatus(supplier.id, supplier.attivo)}
                                        className={`status-toggle ${supplier.attivo ? 'active' : 'inactive'}`}
                                    >
                                        {supplier.attivo ? '✅ Attivo' : '⛔ Disattivo'}
                                    </button>
                                </td>
                                <td>
                                    <div className="action-buttons">
                                        <button className="edit-btn" onClick={() => openEditModal(supplier)}>
                                            ✏️
                                        </button>
                                        <button className="delete-btn" onClick={() => deleteSupplier(supplier.id)}>
                                            🗑️
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {suppliers.length === 0 && !loading && (
                <div className="no-results">
                    <div className="no-results-icon">🏭</div>
                    <p>Nessun fornitore trovato</p>
                    <button className="create-btn" onClick={openCreateModal}>Aggiungi il primo fornitore</button>
                </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <div className="pagination">
                    <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} disabled={pagination.page === 1}>
                        ← Precedente
                    </button>
                    <span>Pagina {pagination.page} di {pagination.totalPages}</span>
                    <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} disabled={pagination.page === pagination.totalPages}>
                        Successiva →
                    </button>
                </div>
            )}

            {/* Modal Creazione/Modifica */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingSupplier ? '✏️ Modifica Fornitore' : '➕ Nuovo Fornitore'}</h3>
                            <button className="close-modal" onClick={() => setShowModal(false)}>✕</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-row">
                                <div className="form-group half">
                                    <label>Nome *</label>
                                    <input
                                        type="text"
                                        value={formData.nome}
                                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                                        placeholder="Nome del fornitore"
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group half">
                                    <label>Partita IVA *</label>
                                    <input
                                        type="text"
                                        value={formData.partita_iva}
                                        onChange={(e) => setFormData({ ...formData, partita_iva: e.target.value })}
                                        placeholder="IT01234567890"
                                        className="form-input"
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group third">
                                    <label>Indirizzo</label>
                                    <input
                                        type="text"
                                        value={formData.indirizzo}
                                        onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                                        placeholder="Via/Piazza..."
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group third">
                                    <label>Città</label>
                                    <input
                                        type="text"
                                        value={formData.citta}
                                        onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group third">
                                    <label>CAP</label>
                                    <input
                                        type="text"
                                        value={formData.cap}
                                        onChange={(e) => setFormData({ ...formData, cap: e.target.value })}
                                        className="form-input"
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group half">
                                    <label>Provincia</label>
                                    <input
                                        type="text"
                                        value={formData.provincia}
                                        onChange={(e) => setFormData({ ...formData, provincia: e.target.value })}
                                        placeholder="PE, CH, TE, AQ"
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group half">
                                    <label>Telefono</label>
                                    <input
                                        type="tel"
                                        value={formData.telefono}
                                        onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                                        className="form-input"
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group half">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group half">
                                    <label>Referente</label>
                                    <input
                                        type="text"
                                        value={formData.contatto}
                                        onChange={(e) => setFormData({ ...formData, contatto: e.target.value })}
                                        placeholder="Persona di contatto"
                                        className="form-input"
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Note</label>
                                <textarea
                                    value={formData.note}
                                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                                    placeholder="Note aggiuntive sul fornitore..."
                                    rows={3}
                                    className="form-textarea"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="cancel-btn" onClick={() => setShowModal(false)}>Annulla</button>
                            <button className="save-btn" onClick={saveSupplier}>💾 Salva Fornitore</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminSuppliers;